/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as DynamicSankeyLayerSpec } from '../../../../layer/preset/other/dynamicSankey';
import { checkFieldType } from '../helper';
import * as utils from '../../../../util';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';

export type Spec = BaseSpec;

type DynamicSankeyLayerType = StandardChartLayerSpec & DynamicSankeyLayerSpec & ILayerTypeSpec;

// 定义桑基图节点接口
interface SankeyNode {
  [key: string]: unknown;
  depth: number;
  root?: boolean;
  itemStyle: Record<string, unknown>;
}

// 创建动态桑基图数据类型验证器，针对特殊类型创建自定义验证函数
// 由于valueText需要支持string或number类型，而baseProcessor中不支持这种复合类型
// 这里我们只验证必要的字段，对于特殊字段在additionalChecks中处理
const validateDynamicSankeyDataTypes = createDataTypeValidator('dynamicSankey', [
  { field: 'name', type: 'string' },
  { field: 'value', type: 'number' },
  { field: 'target', type: 'array' },
  { field: 'date', type: ['string', 'date'] },
  { field: 'describe', type: 'string', required: false },
  { field: 'unit', type: 'string', required: false },
  { field: 'otherInfo', type: 'array', required: false }
]);

// 创建动态桑基图编码验证器
const validateDynamicSankeyEncoding = createEncodingValidator([
  { field: 'name' },
  { field: 'value' },
  { field: 'target' },
  { field: 'date' },
  { field: 'valueText', required: false },
  { field: 'describe', required: false },
  { field: 'unit', required: false },
  { field: 'otherInfo', required: false }
]);

/**
 * 检查节点结构是否符合要求
 */
const validateNodeStructure = (layer: DynamicSankeyLayerType, data: Data) => {
  const { type } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (!values.length) {
    return;
  }

  // 检查节点必须包含depth属性
  const hasDepthProperty = (values as SankeyNode[]).every(item => 'depth' in item);
  if (!hasDepthProperty) {
    utils.logger.warn('warning', `Each node in the "${type}" layer must have a "depth" property`);
    layer.__isEncodingValid = false;
  }

  // 检查是否存在根节点(root=true)
  const hasRootNode = (values as SankeyNode[]).some(item => item.root === true);
  if (!hasRootNode) {
    utils.logger.warn(
      'warning',
      `The "${type}" layer must have at least one root node with "root" set to true`
    );
    layer.__isEncodingValid = false;
  }

  // 检查节点是否包含itemStyle属性
  const hasItemStyleProperty = (values as SankeyNode[]).every(item => 'itemStyle' in item);
  if (!hasItemStyleProperty) {
    utils.logger.warn(
      'warning',
      `Each node in the "${type}" layer must have an "itemStyle" property`
    );
    layer.__isEncodingValid = false;
  }
};

/**
 * 验证特殊字段类型
 */
const validateSpecialFields = (layer: DynamicSankeyLayerType, data: Data) => {
  const { encoding, type } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (!values.length || !encoding.valueText) {
    return;
  }

  // 检查valueText字段 - 字符串或数值类型
  const isString = checkFieldType(values, encoding.valueText, 'string');
  const isNumber = checkFieldType(values, encoding.valueText, 'number');

  if (!isString && !isNumber) {
    utils.logger.warn(
      'warning',
      `The "valueText" field of the "${type}" layer must be a string or number`
    );
    layer.__isEncodingValid = false;
  }
};

// 额外的检查函数
const additionalDynamicSankeyChecks = (layer: DynamicSankeyLayerType, data: Data) => {
  // 检查节点结构
  validateNodeStructure(layer, data);

  // 检查特殊字段类型
  validateSpecialFields(layer, data);
};

// 创建并导出动态桑基图处理器
export const dynamicSankey = createLayerProcessor<DynamicSankeyLayerType>(
  'dynamicSankey',
  validateDynamicSankeyEncoding,
  validateDynamicSankeyDataTypes,
  additionalDynamicSankeyChecks
);
